import React, { useState } from 'react';
import { User, Phone, Mail, Building2, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';

const ContactsPanel = ({ opportunity, token, onUpdate }) => {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', company: '', phone: '', email: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const resetForm = () => setForm({ name: '', role: '', company: '', phone: '', email: '', notes: '' });

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opportunities/${opportunity._id}/contacts`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
      resetForm();
      setAdding(false);
    } catch (e) {
      console.error('Error adding contact', e);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (contactId) => {
    setSaving(true);
    try {
      const { data } = await axios.delete(
        `${getApiUrl()}/opportunities/${opportunity._id}/contacts/${contactId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
    } catch (e) {
      console.error('Error removing contact', e);
    } finally {
      setSaving(false);
    }
  };

  const contacts = opportunity.contacts || [];

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#F0F0F0',
    padding: '8px 10px',
    fontSize: '0.82rem',
    outline: 'none',
    marginBottom: '6px',
    boxSizing: 'border-box',
  };

  return (
    <div>
      {/* Contact list */}
      {contacts.length === 0 && !adding && (
        <p style={{ color: '#444', fontSize: '0.8rem', fontStyle: 'italic' }}>No hay contactos registrados.</p>
      )}

      {contacts.map((c) => (
        <div key={c._id} style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '8px',
          padding: '12px 14px',
          marginBottom: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
              <User size={13} color="#E27B58" />
              <span style={{ fontWeight: '700', color: '#F0F0F0', fontSize: '0.9rem' }}>{c.name}</span>
              {c.role && <span style={{ fontSize: '0.7rem', color: '#888', background: 'rgba(255,255,255,0.06)', padding: '1px 7px', borderRadius: '4px' }}>{c.role}</span>}
            </div>
            {c.company && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#666', marginBottom: '2px' }}>
                <Building2 size={11} /> {c.company}
              </div>
            )}
            {c.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#666', marginBottom: '2px' }}>
                <Phone size={11} /> {c.phone}
              </div>
            )}
            {c.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#666' }}>
                <Mail size={11} /> {c.email}
              </div>
            )}
            {c.notes && <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '6px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '6px' }}>{c.notes}</p>}
          </div>
          <button
            onClick={() => handleRemove(c._id)}
            disabled={saving}
            style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
            onMouseOver={(e) => { e.currentTarget.style.color = '#E74C3C'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = '#555'; }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      {/* Add form */}
      {adding ? (
        <div style={{ background: 'rgba(226,123,88,0.06)', border: '1px solid rgba(226,123,88,0.25)', borderRadius: '8px', padding: '14px', marginTop: '10px' }}>
          <input style={inputStyle} placeholder="Nombre *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input style={inputStyle} placeholder="Rol (Propietario, Agente...)" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
          <input style={inputStyle} placeholder="Empresa" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
          <input style={inputStyle} placeholder="Teléfono" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          <input style={inputStyle} placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '56px' }} placeholder="Notas" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => { setAdding(false); resetForm(); }} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', padding: '6px 14px', cursor: 'pointer', fontSize: '0.8rem' }}>
              <X size={13} /> Cancelar
            </button>
            <button onClick={handleAdd} disabled={saving || !form.name.trim()} style={{ background: '#E27B58', border: 'none', borderRadius: '6px', color: 'white', padding: '6px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8rem', opacity: saving ? 0.7 : 1 }}>
              <Check size={13} /> Guardar
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'transparent', border: '1px dashed rgba(226,123,88,0.4)', borderRadius: '6px', color: '#E27B58', padding: '9px 16px', cursor: 'pointer', fontSize: '0.82rem', width: '100%', justifyContent: 'center', marginTop: '6px' }}>
          <Plus size={14} /> Añadir contacto
        </button>
      )}
    </div>
  );
};

export default ContactsPanel;
